<template>
  <main>
    <section class="collapseList">
      <template v-if="noSocket">
        <div class="empty-state">
          <h4>Você não está conectado ao serviço de notificações.</h4>
          <p>Verifique a conexão do socket para carregar os usuários online.</p>
        </div>
      </template>
      <template v-else>
        <div class="online-panel">
          <header class="online-panel__header">
            <div>
              <small>Monitoramento em tempo real</small>
              <h3>Usuários Online · {{ onlineUsers.length }} ativo(s)</h3>
            </div>
            <div class="online-panel__meta">
              <span>{{ Object.keys(groupedUsers).length }} função(ões)</span>
              <span>{{ unitOptions.length }} unidade(s)</span>
              <span>{{ recentOfflineUsers.length }} saiu(ram) há 30 min</span>
            </div>
          </header>

          <div class="summary-grid" v-if="topFunctionStats.length">
            <button
              v-for="stat in topFunctionStats"
              :key="stat.name"
              type="button"
              :class="{ active: filterNomeFuncao === stat.name }"
              @click="toggleFunctionFilter(stat.name)"
            >
              <strong>{{ stat.count }}</strong>
              <span>{{ stat.name }}</span>
            </button>
          </div>

          <div class="toolbar">
            <label class="search-field">
              <fa :icon="['fas','magnifying-glass']" />
              <input type="search" v-model.trim="search" placeholder="Buscar por nome, e-mail, função ou unidade">
            </label>

            <label>
              <span>Função</span>
              <select v-model="filterNomeFuncao">
                <option value="">Todas</option>
                <option v-for="funcao in uniqueNomeFuncoes" :key="funcao" :value="funcao">{{ funcao }}</option>
              </select>
            </label>

            <label>
              <span>Unidade</span>
              <select v-model="filterUnidade">
                <option value="">Todas</option>
                <option v-for="unidade in unitOptions" :key="unidade" :value="unidade">{{ unidade }}</option>
              </select>
            </label>

            <label>
              <span>Linhas</span>
              <select v-model.number="perPage">
                <option :value="50">50</option>
                <option :value="100">100</option>
                <option :value="200">200</option>
              </select>
            </label>
          </div>

          <div class="people-card">
            <div class="table-headline">
              <strong>{{ filteredUsers.length }} resultado(s)</strong>
              <span>Mostrando {{ pageStart }}-{{ pageEnd }} de {{ filteredUsers.length }}</span>
            </div>

            <div class="people-scroll">
              <div class="people-columns">
                <span>Pessoa</span>
                <span>Função</span>
                <span>Unidade</span>
                <span>Presença</span>
                <span></span>
              </div>
              <ol class="people-list">
                <li v-for="item in paginatedUsers" :key="item.onlineUserId || item.id" class="person-row">
                  <div class="presence-dot"></div>
                  <div class="user-cell">
                    <img v-if="item.urlPic" :src="item.urlPic" alt="Thumbnail">
                    <div v-else class="initials">{{ getInitials(displayName(item)) }}</div>
                    <div>
                      <strong>{{ displayName(item) }}</strong>
                      <span>{{ item.email || '-' }}</span>
                      <small>#{{ item.onlineUserId || item.idUser || item.id }}</small>
                    </div>
                  </div>
                  <div class="person-meta">
                    <span>{{ formatLabel(item.nomeFuncao || 'Sem função') }}</span>
                  </div>
                  <div class="person-unit">
                    <small>{{ item.sigla || 'S/U' }}</small>
                    <span>{{ formatLabel(item.nomeUnidade || '') }}</span>
                    <small v-if="connectionCount(item) > 1">{{ connectionCount(item) }} abas</small>
                  </div>
                  <div class="person-times">
                    <span>Online desde {{ formatTime(item.connectedAt) }}</span>
                    <small>Última interação {{ formatTime(item.lastInteractionAt || item.connectedAt) }}</small>
                  </div>
                  <button class="notification-trigger" type="button" @click="openModal(item)" title="Enviar notificação">
                    <fa :icon="['fal','paper-plane']" />
                  </button>
                </li>
              </ol>
            </div>

            <div class="empty-state compact" v-if="filteredUsers.length === 0">
              <h4>Nenhum usuário encontrado</h4>
              <p>Ajuste os filtros para ampliar a busca.</p>
            </div>

            <footer class="pagination" v-if="filteredUsers.length > 0">
              <button type="button" :disabled="currentPage === 1" @click="currentPage--">Anterior</button>
              <span>Página {{ currentPage }} de {{ totalPages }}</span>
              <button type="button" :disabled="currentPage === totalPages" @click="currentPage++">Próxima</button>
            </footer>
          </div>

          <div class="recent-card" v-if="recentOfflineUsers.length">
            <div class="table-headline">
              <strong>Saíram nos últimos 30 minutos</strong>
              <span>{{ recentOfflineUsers.length }} registro(s)</span>
            </div>
            <ol class="recent-list">
              <li v-for="item in recentOfflineUsers" :key="`recent-${item.onlineUserId || item.id}`">
                <div class="initials muted">{{ getInitials(displayName(item)) }}</div>
                <strong>{{ displayName(item) }}</strong>
                <span>{{ item.email || '-' }}</span>
                <small>Saiu às {{ formatTime(item.disconnectedAt) }}</small>
              </li>
            </ol>
          </div>
        </div>
      </template>

    </section>
    <div v-if="showModal" class="notification-modal" @click.self="closeModal">
      <div class="notification-dialog">
        <header class="notification-dialog__header">
          <div>
            <small>Enviar notificação</small>
            <h3>{{ displayName(selectedUser) }}</h3>
            <span>{{ selectedUser.email }}</span>
          </div>
          <button type="button" class="notification-dialog__close" @click="closeModal">×</button>
        </header>

        <form class="notification-form" @submit.prevent="sendNotification">
          <label>
            <span>Título</span>
            <input
              type="text"
              v-model.trim="notificationMessage.title"
              maxlength="90"
              placeholder="Resumo da notificação"
              autofocus
            >
          </label>

          <label>
            <span>Mensagem</span>
            <textarea
              id="message"
              v-model.trim="notificationMessage.message"
              maxlength="500"
              placeholder="Digite a mensagem que será enviada para o usuário"
            ></textarea>
          </label>

          <footer class="notification-dialog__actions">
            <button type="button" class="button-secondary" @click="closeModal">Cancelar</button>
            <button type="submit" :disabled="isSendingNotification || !canSendNotification">
              {{ isSendingNotification ? 'Enviando...' : 'Enviar notificação' }}
            </button>
          </footer>
        </form>
      </div>
    </div>
  </main>
</template>

<script>
import FerramentasService from '@/services/ferramentas.service.js'

export default {
  data () {
    return {
      list: [],
      filterNomeFuncao: '',
      filterUnidade: '',
      search: '',
      currentPage: 1,
      perPage: 100,
      showModal: false,
      notificationMessage: {
        "type": "apollo",
        "title": null,
        "message": null
      },
      isSendingNotification: false,
      selectedUser: null,
      onlineUserConnections: {},
      recentOffline: {},
      recentTick: Date.now(),
      recentTimer: null,
      noSocket: false
    }
  },
  watch: {
    search () {
      this.currentPage = 1
    },
    filterNomeFuncao () {
      this.currentPage = 1
    },
    filterUnidade () {
      this.currentPage = 1
    },
    perPage () {
      this.currentPage = 1
    },
    filteredUsers () {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages
      }
    }
  },
  mounted () {
    this.recentTimer = setInterval(() => {
      this.recentTick = Date.now()
      this.cleanupRecentOffline()
    }, 60000)

    if (!window.Echo && window.initApolloNotificationsSocket) {
      window.initApolloNotificationsSocket()
    }

    if (window.Echo) {
      window.Echo.join('OnlineUsers').here((users) => {
        this.list = []
        this.onlineUserConnections = {}

        users.forEach(user => {
          this.addOnlineUser(user.id)
        })
      }).joining((user) => {
        this.addOnlineUser(user.id)
      }).leaving((user) => {
        this.removeOnlineUser(user.id)
      })
    } else {
      this.noSocket = true
    }

  },
  beforeUnmount () {
    if (this.recentTimer) {
      clearInterval(this.recentTimer)
    }
  },
  computed: {
    getInitials() {
      return (nome) => {
        if (nome) {
          const words = nome.split(' ');
          const lastWord = words[words.length - 1];
          if (words.length > 1) {
            return `${words[0].charAt(0)}${lastWord.charAt(0)}`;
          }
          return `${words[0].charAt(0)}`;
        }
        return '';
      };
    },
    uniqueNomeFuncoes() {
      return [...new Set(this.onlineUsers.map(item => item.nomeFuncao || 'Sem função'))].sort();
    },
    unitOptions() {
      return [...new Set(this.onlineUsers.map(item => item.sigla).filter(Boolean))].sort();
    },
    recentOfflineUsers() {
      const limit = this.recentTick - (30 * 60 * 1000)
      return Object.values(this.recentOffline)
        .filter(item => item.disconnectedAt && item.disconnectedAt >= limit)
        .sort((a, b) => b.disconnectedAt - a.disconnectedAt)
        .slice(0, 12)
    },
    onlineUsers() {
      const users = {}

      this.list.forEach(item => {
        const key = String(item.onlineUserId || item.idUser || item.id || item.email)

        if (!users[key]) {
          users[key] = item
        }
      })

      return Object.values(users)
        .sort((a, b) => String(this.displayName(a)).localeCompare(String(this.displayName(b))))
    },
    groupedUsers() {
      return this.onlineUsers.reduce((acc, item) => {
        const funcao = item.nomeFuncao || 'Sem função'
        if (!acc[funcao]) {
          acc[funcao] = [];
        }
        acc[funcao].push(item);
        return acc;
      }, {});
    },
    topFunctionStats() {
      return Object.entries(this.groupedUsers)
        .map(([name, users]) => ({ name, count: users.length }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6)
    },
    filteredUsers() {
      const search = this.normalize(this.search)

      return this.onlineUsers.filter(item => {
        if (this.filterNomeFuncao && (item.nomeFuncao || 'Sem função') !== this.filterNomeFuncao) {
          return false
        }

        if (this.filterUnidade && item.sigla !== this.filterUnidade) {
          return false
        }

        if (!search) {
          return true
        }

        const haystack = this.normalize([
          this.displayName(item),
          item.email,
          item.nomeFuncao,
          item.sigla
        ].join(' '))

        return haystack.includes(search)
      })
    },
    totalPages() {
      return Math.max(Math.ceil(this.filteredUsers.length / this.perPage), 1)
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredUsers.slice(start, start + this.perPage)
    },
    pageStart() {
      if (!this.filteredUsers.length) return 0
      return ((this.currentPage - 1) * this.perPage) + 1
    },
    pageEnd() {
      return Math.min(this.currentPage * this.perPage, this.filteredUsers.length)
    },
    canSendNotification() {
      return Boolean(
        this.notificationMessage.title &&
        this.notificationMessage.message &&
        this.selectedUser
      );
    }
  },
  methods: {
    displayName(user) {
      if (!user) return ''

      return user.nomeSocial ||
        user.nomePessoa ||
        user.nomeDocumento ||
        user.nome ||
        user.name ||
        user.email ||
        'Usuário sem nome'
    },
    connectionCount(user) {
      if (!user) return 0

      const userKey = String(user.onlineUserId || user.idUser || user.id)
      return this.onlineUserConnections[userKey] || 1
    },
    formatTime(value) {
      if (!value) return '-'

      const date = new Date(value)
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatLabel(value) {
      return String(value || '')
        .toLocaleLowerCase('pt-BR')
        .replace(/(^|\s|[-/])(\S)/g, match => match.toLocaleUpperCase('pt-BR'))
    },
    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    },
    toggleFunctionFilter(funcao) {
      this.filterNomeFuncao = this.filterNomeFuncao === funcao ? '' : funcao
    },
    cleanupRecentOffline() {
      const limit = Date.now() - (30 * 60 * 1000)

      Object.keys(this.recentOffline).forEach(key => {
        if (this.recentOffline[key].disconnectedAt < limit) {
          delete this.recentOffline[key]
        }
      })
    },
    addOnlineUser(idUser) {
      if (!idUser) return

      const userKey = String(idUser)
      const currentConnections = this.onlineUserConnections[userKey] || 0
      this.onlineUserConnections[userKey] = currentConnections + 1

      if (currentConnections > 0 || this.list.some(item => String(item.onlineUserId || item.idUser || item.id) === userKey)) {
        this.list = this.list.map(item => {
          if (String(item.onlineUserId || item.idUser || item.id) === userKey) {
            return {
              ...item,
              lastInteractionAt: Date.now()
            }
          }

          return item
        })
        return
      }

      FerramentasService.getMeObject(idUser).then(
        response => {
          if (!this.list.some(item => String(item.onlineUserId || item.idUser || item.id) === userKey)) {
            this.list.push({
              ...response.data,
              onlineUserId: idUser,
              connectedAt: Date.now(),
              lastInteractionAt: Date.now()
            })
          }
        },
        () => {}
      )
    },
    removeOnlineUser(idUser) {
      if (!idUser) return

      const userKey = String(idUser)
      const currentConnections = this.onlineUserConnections[userKey] || 0
      const nextConnections = Math.max(currentConnections - 1, 0)

      if (nextConnections > 0) {
        this.onlineUserConnections[userKey] = nextConnections
        return
      }

      delete this.onlineUserConnections[userKey]
      const removedUser = this.list.find(item => String(item.onlineUserId || item.idUser || item.id) === userKey)
      if (removedUser) {
        this.recentOffline[userKey] = {
          ...removedUser,
          disconnectedAt: Date.now()
        }
      }

      this.list = this.list.filter(item => String(item.onlineUserId || item.idUser || item.id) !== userKey)
    },
    openModal(user) {
      this.selectedUser = user;
      this.notificationMessage = {
        type: 'apollo',
        title: null,
        message: null
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.selectedUser = null;
      this.isSendingNotification = false;
    },
    async sendNotification() {
      if (!this.canSendNotification || this.isSendingNotification) {
        return;
      }

      const data = {
        "userId": this.selectedUser.onlineUserId || this.selectedUser.id,
        "type": "apollo",
        "title": this.notificationMessage.title,
        "msg": this.notificationMessage.message,
        "forAll": false,
        "idFuncao": null,
        "OnlineOnly": null
      };

      this.isSendingNotification = true;

      try {
        await FerramentasService.sendNotification(data);
        this.closeModal();
      } catch (error) {
        this.isSendingNotification = false;
      }
    }
  }
}
</script>


<style lang="scss" scoped>
  main {
    padding: 8px 24px 20px;
  }

  .collapseList {
    padding: 0;
  }

  .initials {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 30px;
    height: 30px;
    min-width: 30px;
    border-radius: 50%;
    background: #c7ced5;
    color: white;
    font-weight: bold;
    font-size: 12px;
  }

  .online-panel {
    display: grid;
    gap: 8px;
  }

  .online-panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 14px;
    background: #ffffff;
    border: 1px solid #dbe5ee;
    border-radius: 8px;
    box-shadow: none;

    small {
      display: block;
      margin-bottom: 2px;
      color: #607382;
      font-size: 10px;
      font-weight: 800;
      letter-spacing: .04em;
      text-transform: uppercase;
    }

    h3 {
      margin: 0;
      color: #08243a;
      font-size: 18px;
      font-weight: 800;
      line-height: 1.1;
    }
  }

  .online-panel__meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;

    span {
      display: inline-flex;
      align-items: center;
      height: 24px;
      padding: 0 9px;
      color: #31556d;
      font-size: 10px;
      font-weight: 800;
      background: #eef6fb;
      border: 1px solid #d5e6f0;
      border-radius: 999px;
    }
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 6px;

    button {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
      min-height: 42px;
      padding: 7px 10px;
      color: #24475f;
      text-align: left;
      background: #ffffff;
      border: 1px solid #dbe5ee;
      border-radius: 10px;
      cursor: pointer;
      transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease;

      strong {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 34px;
        height: 26px;
        padding: 0 8px;
        color: #08243a;
        font-size: 15px;
        font-weight: 900;
        background: #eaf4f8;
        border-radius: 8px;
      }

      span {
        min-width: 0;
        overflow: hidden;
        color: #607382;
        font-size: 11px;
        font-weight: 800;
        line-height: 1.25;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &:hover,
      &.active {
        border-color: #08a9c6;
        box-shadow: 0 10px 24px rgba(8,169,198,.12);
        transform: translateY(-1px);
      }
    }
  }

  .toolbar {
    display: grid;
    grid-template-columns: minmax(280px, 1fr) 220px 160px 110px;
    gap: 10px;
    align-items: end;
    padding: 8px;
    background: #ffffff;
    border: 1px solid #dbe5ee;
    border-radius: 10px;

    label {
      display: grid;
      gap: 6px;
      margin: 0;

      > span {
        color: #42657d;
        font-size: 10px;
        font-weight: 800;
        text-transform: uppercase;
      }
    }

    input,
    select {
      width: 100%;
      height: 36px;
      padding: 0 10px;
      color: #08243a;
      background: #f8fbfd;
      border: 1px solid #cfe0eb;
      border-radius: 8px;
      outline: none;
    }

    input:focus,
    select:focus {
      border-color: #08a9c6;
      box-shadow: 0 0 0 3px rgba(8,169,198,.12);
    }
  }

  .search-field {
    position: relative;

    svg {
      position: absolute;
      bottom: 10px;
      left: 12px;
      color: #8ba0ae;
      font-size: 13px;
    }

    input {
      padding-left: 34px;
    }
  }

  .people-card,
  .recent-card {
    overflow: hidden;
    background: #ffffff;
    border: 1px solid #dbe5ee;
    border-radius: 10px;
    box-shadow: 0 14px 34px rgba(12,34,52,.06);
  }

  .table-headline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 14px;
    border-bottom: 1px solid #e6eef5;

    strong {
      color: #08243a;
      font-size: 13px;
      font-weight: 900;
    }

    span {
      color: #708493;
      font-size: 11px;
      font-weight: 700;
    }
  }

  .people-scroll {
    max-height: calc(100vh - 245px);
    min-height: 520px;
    overflow: auto;
    @extend %scrollbar_light;
  }

  .people-columns {
    position: sticky;
    top: 0;
    z-index: 2;
    display: grid;
    grid-template-columns: minmax(300px, 1.2fr) minmax(220px, .8fr) minmax(160px, .55fr) 190px 42px;
    gap: 14px;
    padding: 8px 14px 8px 32px;
    color: #42657d;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    background: #f8fbfd;
    border-bottom: 1px solid #edf3f7;
  }

  .people-list,
  .recent-list {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  .person-row {
    display: grid;
    grid-template-columns: 10px minmax(280px, 1.2fr) minmax(220px, .8fr) minmax(160px, .55fr) 190px 42px;
    align-items: center;
    gap: 14px;
    min-height: 56px;
    padding: 8px 14px;
    border-bottom: 1px solid #edf3f7;
    transition: background .15s ease;

    &:hover {
      background: #f8fbfd;
    }
  }

  .presence-dot {
    width: 8px;
    height: 8px;
    background: #22c55e;
    border-radius: 50%;
    box-shadow: 0 0 0 4px rgba(34,197,94,.12);
  }

  .user-cell {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 260px;

    img {
      width: 30px;
      height: 30px;
      min-width: 30px;
      border-radius: 50%;
      object-fit: cover;
      border: 1px solid #dbe5ee;
    }

    strong {
      display: block;
      max-width: 330px;
      overflow: hidden;
      color: #24475f;
      font-size: 12px;
      font-weight: 800;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    span {
      display: block;
      max-width: 360px;
      overflow: hidden;
      color: #708493;
      font-size: 11px;
      font-weight: 600;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      display: block;
      margin-top: 1px;
      color: #9aa9b4;
      font-size: 10px;
      font-weight: 800;
    }
  }

  .person-meta {
    min-width: 0;

    > span {
      display: block;
      overflow: hidden;
      color: #42657d;
      font-size: 11px;
      font-weight: 800;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .person-unit {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    > span {
      overflow: hidden;
      color: #708493;
      font-size: 10px;
      font-weight: 700;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 36px;
      height: 22px;
      padding: 0 8px;
      color: #31556d;
      font-size: 10px;
      font-weight: 900;
      background: #eef6fb;
      border-radius: 999px;
    }
  }

  .person-times {
    display: grid;
    gap: 2px;
    color: #708493;
    font-size: 10px;
    font-weight: 700;
    text-align: right;

    small {
      color: #91a1ad;
      font-size: 10px;
      font-weight: 700;
    }
  }

  .unit-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 42px;
    height: 24px;
    padding: 0 8px;
    color: #31556d;
    font-size: 11px;
    font-weight: 900;
    background: #eef6fb;
    border-radius: 999px;
  }

  .pagination {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 12px 16px;
    border-top: 1px solid #e6eef5;

    span {
      color: #607382;
      font-size: 12px;
      font-weight: 700;
    }

    button {
      height: 32px;
      padding: 0 12px;
      color: #24475f;
      font-size: 12px;
      font-weight: 800;
      background: #f8fbfd;
      border: 1px solid #cfe0eb;
      border-radius: 8px;
      cursor: pointer;

      &:disabled {
        cursor: not-allowed;
        opacity: .45;
      }
    }
  }

  .empty-state {
    padding: 32px;
    color: #607382;
    text-align: center;
    background: #ffffff;
    border: 1px solid #dbe5ee;
    border-radius: 10px;

    &.compact {
      margin: 12px;
      padding: 24px;
    }

    h4 {
      margin: 0 0 6px 0;
      color: #08243a;
      font-size: 16px;
      font-weight: 900;
    }

    p {
      margin: 0;
      font-size: 12px;
    }
  }

  .notification-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 30px;
    min-width: 30px;
    height: 30px;
    padding: 0;
    color: #42657d;
    font-size: 12px;
    font-weight: 800;
    line-height: 1;
    white-space: nowrap;
    background: #ffffff;
    border: 1px solid #cfe0eb;
    border-radius: 8px;
    box-shadow: none;
    cursor: pointer;
    transition: color .15s ease, background .15s ease, border-color .15s ease;

    span {
      display: none;
    }
  }

  .notification-trigger:hover {
    color: #08243a;
    background: #eef6fb;
    border-color: #9fc9db;
  }

  .notification-trigger:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(8, 169, 198, 0.12);
  }

  .recent-card {
    margin-top: 2px;
  }

  .recent-list {
    display: grid;
    gap: 0;
    max-height: 220px;
    overflow: auto;
    @extend %scrollbar_light;

    li {
      display: grid;
      grid-template-columns: 30px minmax(180px, 1fr) minmax(220px, 1fr) 90px;
      align-items: center;
      gap: 10px;
      min-height: 42px;
      padding: 6px 14px;
      border-top: 1px solid #edf3f7;
    }

    strong,
    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    strong {
      color: #42657d;
      font-size: 11px;
      font-weight: 800;
    }

    span,
    small {
      color: #708493;
      font-size: 10px;
      font-weight: 700;
    }

    small {
      text-align: right;
    }
  }

  .initials.muted {
    background: #dbe3ea;
  }

  .notification-modal {
    position: fixed;
    z-index: 10000;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    width: 100%;
    height: 100%;
    background: rgba(11, 27, 43, 0.42);
    backdrop-filter: blur(2px);
  }

  .notification-dialog {
    width: min(560px, 100%);
    overflow: hidden;
    background: #fff;
    border: 1px solid #d7e4ee;
    border-radius: 8px;
    box-shadow: 0 22px 70px rgba(11, 27, 43, 0.24);
  }

  .notification-dialog__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 22px 24px 18px;
    border-bottom: 1px solid #e6eef5;
    background: #f8fbfd;
  }

  .notification-dialog__header small {
    display: block;
    margin-bottom: 6px;
    color: #58758b;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
  }

  .notification-dialog__header h3 {
    margin: 0 0 4px;
    color: #06233b;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
  }

  .notification-dialog__header span {
    color: #6f8799;
    font-size: 13px;
  }

  .notification-dialog__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    color: #42657d;
    font-size: 24px;
    line-height: 1;
    background: #fff;
    border: 1px solid #d4e3ee;
    border-radius: 8px;
    cursor: pointer;
  }

  .notification-dialog__close:hover {
    color: #082238;
    background: #edf6fb;
  }

  .notification-form {
    display: grid;
    gap: 18px;
    padding: 22px 24px 24px;
  }

  .notification-form label {
    display: grid;
    gap: 8px;
    margin: 0;
  }

  .notification-form label span {
    color: #385970;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
  }

  .notification-form input,
  .notification-form textarea {
    width: 100%;
    color: #09243a;
    background: #fff;
    border: 1px solid #c9dce8;
    border-radius: 6px;
    outline: none;
    transition: border-color .15s ease, box-shadow .15s ease;
  }

  .notification-form input {
    height: 42px;
    padding: 0 12px;
  }

  .notification-form textarea {
    min-height: 132px;
    padding: 12px;
    resize: vertical;
  }

  .notification-form input:focus,
  .notification-form textarea:focus {
    border-color: #08a9c6;
    box-shadow: 0 0 0 3px rgba(8, 169, 198, 0.12);
  }

  .notification-dialog__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 2px;
  }

  .notification-dialog__actions button {
    min-width: 128px;
    height: 42px;
    padding: 0 16px;
    color: #fff;
    font-weight: 700;
    background: #ff0036;
    border: 0;
    border-radius: 999px;
    cursor: pointer;
  }

  .notification-dialog__actions button:disabled {
    cursor: not-allowed;
    opacity: .55;
  }

  .notification-dialog__actions .button-secondary {
    color: #24475f;
    background: #eef5f9;
    border: 1px solid #cfe0eb;
  }

  @media (max-width: 640px) {
    main {
      padding: 16px;
    }

    .online-panel__header,
    .table-headline,
    .pagination {
      align-items: flex-start;
      flex-direction: column;
    }

    .summary-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .toolbar {
      grid-template-columns: 1fr;
    }

    .people-scroll {
      max-height: none;
      min-height: 0;
    }

    .person-row {
      grid-template-columns: 10px 1fr 34px;
      gap: 10px;

      .person-meta,
      .person-unit,
      .person-times {
        grid-column: 2 / 4;
        text-align: left;
      }
    }

    .people-columns {
      display: none;
    }

    .recent-list li {
      grid-template-columns: 30px 1fr;

      span,
      small {
        grid-column: 2;
        text-align: left;
      }
    }

    .notification-modal {
      align-items: flex-end;
      padding: 16px;
    }

    .notification-dialog__actions {
      flex-direction: column-reverse;
    }

    .notification-dialog__actions button {
      width: 100%;
    }
  }
</style>
